import AddIcon from '@mui/icons-material/AddCircle';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Divider, FormControl, IconButton, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import TextField from "@mui/material/TextField/TextField";
import { useTheme } from "@mui/styles";
import moment from "moment";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, MouseEvent, useEffect, useRef, useState } from "react";
import * as Yup from 'yup';
import AttachmentPreviewBox from "../../../components/AttachmentPreviewBox";
import CommentFormDialog from "../../../components/CommentFormDialog";
import DefaultButton from "../../../components/FormControl/DefaultButton";
import ErrorButton from "../../../components/FormControl/ErrorButton";
import Form, { FormDirty, FormErrors, FormFooterAllButtons, FormValidationSchema } from "../../../components/FormControl/Form/Form";
import FormDialog from '../../../components/FormControl/FormDialog';
import MultiSelect, { MultiSelectOption } from '../../../components/MultiSelect';
import Panel from "../../../components/Panel/Panel";
import PanelBodyFormContent from "../../../components/Panel/PanelBodyFormContent";
import SearchEntity from "../../../components/SelectSearch/SearchEntity";
import TicketHistory, { TicketHistoryEventEmitterType } from "../../../components/TicketHistory";
import AttachmentTaskType from "../../../enum/AttachmentTaskType";
import CommentTaskType from "../../../enum/CommentTaskType";
import UserType from "../../../enum/UserType";
import AttachmentTask from "../../../model/AttachmentTask";
import CommentTask from "../../../model/CommentTask";
import FinalCustomer from "../../../model/FinalCustomer";
import Task from "../../../model/Task";
import TicketCause from "../../../model/TicketCause";
import TicketSolutionType from "../../../model/TicketSolutionType";
import TicketStatus from "../../../model/TicketStatus";
import User from "../../../model/User";
import { useAuthentication } from "../../../reducer/authentication";
import { useLoading } from "../../../reducer/loading";
import { WhereCondition, downloadBlob } from "../../../service/ServiceApi";
import { ServiceApi } from "../../../service/ServiceApiWithLoading";
import { convertFileByteToSizeString, objectClone, sortObjectArrayByProp } from "../../../utils";
import EventEmitter from "../../../utils/EventEmitter";
import { ServiceTimeCounter } from '../../../components/ServiceTimeCounter';
import CommentTaskUser from '../../../model/CommentTaskUser';
import SafeDeskTextEditor from '../../../components/SafeDeskTextEditor';

const apiFinalCustomer = new ServiceApi<FinalCustomer>('finalCustomer');
const apiTicketStatus = new ServiceApi<TicketStatus>('ticketStatus');
const apiUser = new ServiceApi<User>('user');
const api = new ServiceApi<Task>('task');

const KeyCodes = {
    comma: 188,
    enter: 13
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

export interface AttachmentTicketsRow extends AttachmentTask {
    edited?: boolean;
    commentIdx?: number;
}

interface ReactTagsData {
    id: string;
    text: string;
}

export type TicketsFormData = {
    name: string;
    description: string;
    final_customer_id: number | null;
    customer_contacts: number[];
    attachments: AttachmentTicketsRow[];
    comments: CommentTask[];
}

export interface TicketsFormProps {
    title: React.ReactNode;
    item?: Task;
    onSubmit: (data: TicketsFormData, isDirty?: boolean, submitType?: string | null) => Promise<any>;
    onCancel: () => any;
    onChange?: (changed: boolean) => any;
    onUndo?: () => any;
}

interface TicketsAttachmentListProps {
    attachments: AttachmentTicketsRow[];
    downloadAttachment: (name: string, systemAttachmentId?: number | null, systemAttachmentFile?: string) => void;
    setFieldValue: any;
    openAttachmentDescriptionDialog: (idx: number) => void;
    removeAttachment: (idx: number, attachments: AttachmentTicketsRow[], setFieldValue: any) => void;
    type: AttachmentTaskType;
    addAttachment: (attachments: AttachmentTicketsRow[], type: AttachmentTaskType, setFieldValue: any, name: string, file?: File) => void;
    title: string;
    isDisabled?: boolean;
}

export function TicketsAttachmentList(props: TicketsAttachmentListProps) {
    const { attachments, downloadAttachment, setFieldValue, openAttachmentDescriptionDialog,
        removeAttachment, type, addAttachment, title, isDisabled } = props;
    let contadorAttachments = 0;

    var refInputFile = React.createRef<HTMLInputElement>();

    function inputChangeHandler(e: ChangeEvent<HTMLInputElement>) {
        const input = e.target;
        if (input?.files && input.files.length > 0) {
            Array.from(input.files).forEach(file => {
                addAttachment(attachments, type, setFieldValue, file.name, file);
            });
        }
    }

    function buttonClickHandler(e: MouseEvent<HTMLButtonElement>) {
        const input = refInputFile.current;
        input?.click();
    }

    return (<>
        <Box marginTop={3} marginBottom={0} display="flex" >
            <Typography style={{ flex: 1 }} variant='subtitle2' className="subtitleGradientBackgorund">{title}</Typography>
            {!isDisabled ? <Tooltip placement="top" title={'Adicionar anexo'}>
                <IconButton
                    size='small'
                    color="inherit"
                    aria-label={'Adicionar anexo'}
                    onClick={buttonClickHandler}
                >
                    <input
                        ref={refInputFile}
                        type='file'
                        style={{ display: 'none' }}
                        onChange={inputChangeHandler}
                        multiple={true}
                        accept={"*"}
                        id={`attachment-task-${type}`}
                    />
                    <AddIcon />
                </IconButton>
            </Tooltip> : null}
        </Box>
        <Divider style={{ marginBottom: 4 }} />
        {attachments.map((at, idx) => {
            return at.type === type ? <Box key={idx + '-' + type} marginBottom={1} display="flex">
                <div style={{ fontWeight: 'bold', display: 'flex', width: 35, alignItems: 'center' }} >#{`${++contadorAttachments}`}</div>
                <Box marginBottom={1} display="flex" flexDirection="column" flex={1} margin={0} overflow="hidden">
                    <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                        <AttachmentPreviewBox file={at.file} id={at.task_id || null} model="Task" name={at.name} systemAttachmentId={at.system_attachment_id} systemAttachmentFile={at.system_attachment?.file} />
                        <Typography style={{ flex: 1, alignItems: 'center', display: 'flex', overflow: 'hidden', paddingLeft: 5 }} title={at.name} variant='body2'>{at.name}</Typography>
                        {(at.system_attachment?.file_size || at.file_size) ? <Typography style={{ alignItems: 'center', display: 'flex', overflow: 'hidden', paddingRight: 5, paddingLeft: 5 }} variant='body2'>{convertFileByteToSizeString((at.system_attachment?.file_size || at.file_size)!)}</Typography> : null}
                        {at.name && at.id > 0 ? <>
                            <Tooltip placement="top" title={'Download do anexo'}>
                                <IconButton
                                    size='small'
                                    color="inherit"
                                    aria-label={'Download do anexo'}
                                    onClick={() => downloadAttachment(at.name, at.system_attachment_id, at.system_attachment?.file)}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </> : null}
                        {!isDisabled ? <>
                            <Tooltip placement="top" title={'Adicionar informação'}>
                                <IconButton
                                    size='small'
                                    color="inherit"
                                    aria-label={'Adicionar informação'}
                                    onClick={() => openAttachmentDescriptionDialog(idx)}
                                >
                                    <ChatIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip placement="top" title={'Remover anexo'}>
                                <IconButton
                                    size='small'
                                    color="inherit"
                                    aria-label={'Remover anexo'}
                                    onClick={() => removeAttachment(idx, attachments, setFieldValue)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </> : null}
                    </div>
                    {at.description ? <Typography style={{ flex: 1 }} variant='caption'>{at.description}</Typography> : null}
                </Box>
            </Box> : null;
        })}
    </>);
}

export interface TicketsAttachmentDescriptionFormDialogProps {
    attachmentDescriptionDialogOpen: boolean;
    onSubmit: (description: string) => any;
    onCancel: () => any;
    attachments: AttachmentTicketsRow[];
    attachmentsEditingIdx: number | null;
}

interface TicketsAttachmentDescriptionFormDialogData {
    description: string;
}

export function TicketsAttachmentDescriptionFormDialog(props: TicketsAttachmentDescriptionFormDialogProps) {

    const { attachmentDescriptionDialogOpen, onCancel, attachments, attachmentsEditingIdx, onSubmit } = props;

    function onSubmitHandler(data: TicketsAttachmentDescriptionFormDialogData) {
        onSubmit(data.description);
    }

    function getDescription(): string {
        if (attachmentsEditingIdx === null) return '';

        return attachments[attachmentsEditingIdx].description;
    }

    return (
        <FormDialog<TicketsAttachmentDescriptionFormDialogData>
            show={attachmentDescriptionDialogOpen}
            title={'Descrição do anexo'}
            dismiss={() => null}
            onSubmit={(values, errors) => {
                const { description } = values;
                onSubmitHandler({ description });
            }}
            initialValues={{ description: getDescription() }}
            enableReinitialize={true}
            maxWidth='sm'
            fullWidth
        >
            {({ values, errors, handleChange, setFieldValue, isSubmitting, dirty }) => (
                <>
                    <FormControl fullWidth variant="outlined" margin='dense'>
                        <TextField
                            name="description"
                            label={"Descrição"}
                            margin="dense"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                            value={values.description}
                            onChange={handleChange}
                            error={!!errors.description && !!dirty.description}
                            helperText={!!errors.description && !!dirty.description && errors.description}
                        />
                    </FormControl>
                    <FormControl fullWidth variant="outlined" margin="dense" style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                        <DefaultButton
                            size='small'
                            type='submit'
                            variant="contained"
                            disabled={isSubmitting}>
                            OK
                        </DefaultButton>
                        <ErrorButton
                            size='small'
                            variant="contained"
                            disabled={isSubmitting}
                            onClick={() => onCancel()}
                            style={{ marginLeft: 10 }}>
                            Cancelar
                        </ErrorButton>
                    </FormControl>
                </>
            )}
        </FormDialog>
    );
}

const eventEmmiter = new EventEmitter<TicketHistoryEventEmitterType>();

export default function TicketsForm(props: TicketsFormProps) {
    const { title, item, onSubmit, onCancel, onChange, onUndo } = props;

    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthentication();
    const theme = useTheme();
    const setLoading = useLoading();

    const [customerContacts, setCustomerContacts] = useState<MultiSelectOption<number>[]>([]);
    const [attachmentsRemoved, setAttachmentsRemoved] = useState<AttachmentTicketsRow[]>([]);
    const [attachmentsEditingIdx, setAttachmentsEditingIdx] = useState<number | null>(null);
    const [attachmentDescriptionDialogOpen, setAttachmentDescriptionDialogOpen] = useState<boolean>(false);
    const [commentDialogOpen, setCommentDialogOpen] = useState<boolean>(false);
    const [isDirty, setDirty] = useState<boolean>(false);
    const [isStatusClosed, setIsStatusClosed] = useState<boolean>(false);
    const commentDialogOpenRef = useRef<boolean>(false);
    commentDialogOpenRef.current = commentDialogOpen;

    const [heightDescription, setHeightDescription] = useState<number>(165);
    const [heightSolution, setHeightSolution] = useState<number>(165);

    useEffect(() => {
        window.addEventListener('paste', onPasteHandlerTask);
        setRulesByStatus(item?.ticket_status_id);
        fetchDataByFinalCustomer(item?.final_customer_id || user?.final_customer_id, true);

        if (item?.description) {
            let countLinesDescription = item.description.split(/\r?\n/).length;
            if (countLinesDescription > 7) {
                if (countLinesDescription > 9) {
                    setHeightDescription(225);
                } else if (countLinesDescription > 8) {
                    setHeightDescription(205);
                } else {
                    setHeightDescription(185);
                }
            }
        }

        if (item?.solution) {
            let countLinesSolution = item.solution.split(/\r?\n/).length;
            if (countLinesSolution > 7) {
                if (countLinesSolution > 9) {
                    setHeightSolution(225);
                } else if (countLinesSolution > 8) {
                    setHeightSolution(205);
                } else {
                    setHeightSolution(185);
                }
            }
        }

        return () => {
            window.removeEventListener('paste', onPasteHandlerTask);
        }
    }, []);

    function onPasteHandlerTask(evt: ClipboardEvent | Event) {
        const clipboardItems = (evt as ClipboardEvent).clipboardData?.items;
        if (!clipboardItems) {
            return;
        }
        const container = new DataTransfer();
        for (const item of Array.from(clipboardItems)) {
            let blob = item.getAsFile();

            if (!blob) continue;

            /*let imageEle = document.getElementById('preview') as HTMLImageElement;
            if (imageEle && blob) {
                imageEle.src = URL.createObjectURL(blob);
            }*/

            let file = new File([blob], blob.name, {
                type: blob.type,
                lastModified: new Date().getTime()
            });
            container.items.add(file);
        }

        if (container.files.length === 0) return;
        evt.preventDefault();
        evt.stopPropagation();
        if (commentDialogOpenRef.current === true) {
            onPasteHandlerContinue(container.files, 'comment_attachment');
        } else if (!item?.id) {
            onPasteHandlerContinue(container.files, `attachment-task-${AttachmentTaskType.Client}`);
        }
    }

    function onPasteHandlerContinue(clipboardItems: FileList | null, inputId: string) {
        if (!clipboardItems) {
            return;
        }

        const fileInput = document.querySelector<HTMLInputElement>('#' + inputId);
        if (fileInput) {
            fileInput.files = clipboardItems;

            // Trigger the onChange event
            const event = new Event('change', { bubbles: true });
            fileInput.dispatchEvent(event);
        }
    }

    async function fetchDataByFinalCustomer(final_customer_id?: number | null, loadingTask?: boolean, setFieldValue?: any) {
        if (final_customer_id) {
            const finalCustomer = await apiFinalCustomer.getById(final_customer_id, { relations: ['products', 'users', 'integrator', 'integrator.users'] });
            let users = [...(finalCustomer.users || []), ...(finalCustomer.integrator?.users || [])];
            let usersSorted = sortObjectArrayByProp(users, 'name');

            let autoUseContactUsers: number[] = [];
            if (user?.id) {
                autoUseContactUsers.push(user.id);
            }
            setCustomerContacts(usersSorted.map((c) => {
                let userName = `${c.name}`;
                if (c.type === UserType.Integrator) {
                    userName += ` - ${finalCustomer.integrator?.name}`;
                } else if (c.type === UserType.FinalCustomer) {
                    userName += `  - ${finalCustomer.name}`;
                } else {
                    userName += ` - Commbox`;
                }
                if (c.auto_use_contact) {
                    autoUseContactUsers.push(c.id);
                }
                return { value: c.id, label: userName, disabled: c.auto_use_contact === 2 || (!item && c.id === user?.id) || c.id === item?.created_by_user_id };
            }));

            if (!loadingTask && setFieldValue) {
                setFieldValue('customer_contacts', autoUseContactUsers);
            }
        } else {
            setCustomerContacts([]);
        }
    }

    const initialValues: TicketsFormData = {
        name: item?.name || '',
        description: item?.description || '',
        final_customer_id: item?.final_customer_id || user?.final_customer_id || null,
        customer_contacts: item?.customer_contacts ? item.customer_contacts.map(c => c.id) : (user?.id ? [user.id] : []),
        comments: item?.comments?.filter(c => c.type === CommentTaskType.PreService) || [],
        attachments: item?.attachments ? objectClone(item?.attachments) : []
    };

    function getValidationSchema() {
        const validationSchema: FormValidationSchema<TicketsFormData> = {
            name: Yup.string()
                .trim()
                .required()
                .min(3)
                .max(80)
                .label('Assunto'),
            final_customer_id: Yup.number()
                .required()
                .label('Cliente final'),
            description: Yup.string()
                .trim()
                .required()
                .label('Descrição')
        }

        return validationSchema;
    }

    async function onSubmitHandler(values: TicketsFormData, dirty: FormDirty<TicketsFormData>, errors?: FormErrors<TicketsFormData>, submitType?: string | null) {
        const isDirty = Object.values(dirty).reduce((p, c) => p || c, false);

        if (errors) {
            for (var error of Object.values(errors).filter(e => !!e)) {
                enqueueSnackbar(error, { variant: 'error' });
            }
            return;
        }

        if (item?.id) {
            const countHasUpdated = await api.count({ where: [['id', '=', item.id], ['updated_at', '>', moment(item.updated_at).format('YYYY-MM-DD HH:mm:ss.SSS')]] });
            if (countHasUpdated > 0) {
                enqueueSnackbar("Esse registro foi alterado por outro usuário. É necessário recarregar a página.", { variant: 'error', autoHideDuration: 10000 });
                return;
            }
        }
        await onSubmit(values, isDirty, submitType);
    }

    function addAttachment(attachments: AttachmentTicketsRow[], type: AttachmentTaskType, setFieldValue: any, name: string, file?: File, commentIdx?: number, system_attachment_id?: number | null, system_attachment?: any) {
        if (attachments.filter((a) => a.type === type).length < 10) {
            attachments.push({ id: 0, description: '', name, type, file, commentIdx, system_attachment_id, system_attachment });
            setFieldValue('attachments', attachments);
        } else {
            enqueueSnackbar("Limite de 10 anexos atingido.", { variant: 'error', preventDuplicate: true });
        }
    }

    function removeAttachment(idx: number, attachments: AttachmentTicketsRow[], setFieldValue: any) {
        let attachment = attachments.find((o, i) => i === idx);
        setFieldValue('attachments', attachments.filter((o, i) => i !== idx));
        if (attachment && attachment.id > 0) {
            attachmentsRemoved.push(attachment);
            setAttachmentsRemoved(attachmentsRemoved);
        }
    }

    async function downloadAttachment(fileName: string, systemAttachmentId?: number | null, systemAttachmentFile?: string) {
        if (systemAttachmentId && systemAttachmentFile) {
            let dir = `SystemAttachment/${systemAttachmentId}/${systemAttachmentFile}`;
            await downloadBlob(dir, fileName);
        } else {
            let dir = `Task/${item?.id}/${fileName}`;
            await downloadBlob(dir, fileName);
        }
    }

    function openAttachmentDescriptionDialog(idx: number) {
        setAttachmentsEditingIdx(idx);
        setAttachmentDescriptionDialogOpen(true);
    }

    function closeAttachmentDescriptionDialog() {
        setAttachmentsEditingIdx(null);
        setAttachmentDescriptionDialogOpen(false);
    }

    function submitAttachmentDescriptionDialog(setFieldValue: any, attachments: AttachmentTicketsRow[], description: string, idx: number | null) {
        if (idx === null) return;
        attachments[idx].description = description;
        attachments[idx].edited = true;
        setFieldValue('attachments', attachments);
        setAttachmentDescriptionDialogOpen(false);
        setAttachmentsEditingIdx(null);
    }

    function openCommentDialog() {
        setCommentDialogOpen(true);
    }

    function closeCommentDialog() {
        setCommentDialogOpen(false);
    }

    async function submitCommentDialog(setFieldValue: any, comments: CommentTask[], comment: string, is_public: boolean,
        time?: string, assigned_users?: number[] | null, newAttachments?: AttachmentTask[], attachments?: AttachmentTicketsRow[], forceUser?: User) {
        if (comment.trim() !== '') {
            var commentTaskUsers: CommentTaskUser[] | undefined = undefined;
            if (assigned_users && assigned_users.length > 0) {
                const users = await apiUser.getList({ where: [['id', 'in', assigned_users]], order: { name: 'ASC' } });
                if (users && users.length > 0) {
                    commentTaskUsers = users.map(u => { return { id: 0, comment_task_id: 0, user_id: u.id, user: u } });
                }
            }
            let newComment: CommentTask = {
                id: 0,
                comment,
                user_id: forceUser ? forceUser.id : (user?.id ? user?.id : 0),
                user: forceUser ? forceUser : undefined,
                is_public,
                type: CommentTaskType.PreService,
                comment_task_users: commentTaskUsers
            };
            let commentIdx = comments.push(newComment);
            commentIdx = commentIdx - 1;
            eventEmmiter.emit('newComment', newComment);
            await setFieldValue('comments', comments);

            if (attachments && newAttachments && newAttachments.length > 0) {
                newAttachments.forEach(attachment => {
                    addAttachment(attachments, attachment.type, setFieldValue, attachment.name, attachment.file, commentIdx, attachment.system_attachment_id, attachment.system_attachment);
                })
            }
        }
        setCommentDialogOpen(false);
    }

    function setChanges(changed: boolean) {
        setDirty(changed);
        if (onChange) onChange(changed);
    }

    var extraWhereUserId: WhereCondition<User> = [];
    extraWhereUserId.push(['active', '=', true]);
    extraWhereUserId.push(['type', '=', UserType.Commbox]);

    var extraWhereTicketStatusId: WhereCondition<User> = [];
    extraWhereTicketStatusId.push(['use_in_task', '=', true]);

    if (!!user?.allow_edit_task_resolution === false) {
        extraWhereTicketStatusId.push(['closed', '=', false]);
    }

    async function setRulesByStatus(ticket_status_id?: number | null) {
        let disableComment = false;
        let isClosed = false;
        if (ticket_status_id) {
            const ticketStatus = await apiTicketStatus.getById(ticket_status_id);
            disableComment = ticketStatus.disable_comment;
            isClosed = ticketStatus.closed;
        }
        setIsStatusClosed(isClosed);
    }

    return (
        <Panel
            panelHeaderTitle={title}
            panelFooterButtons={<FormFooterAllButtons onUndo={onUndo} form="task_form" onBack={onCancel} isDirty={isDirty} enableSaveAndCreateNew={false} />}
        >
            <PanelBodyFormContent style={{ overflow: 'auto' }}>
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Form<TicketsFormData>
                            initialValues={initialValues}
                            validationSchema={getValidationSchema()}
                            onSubmit={onSubmitHandler}
                            onChange={setChanges}
                            style={{ overflow: 'auto' }}
                            id="task_form"
                        >
                            {({ isSubmitting, values, errors, handleChange, dirty, setFieldValue }) => {
                                return (
                                    <>
                                        <Box marginTop={3} marginBottom={0}>
                                            <Typography variant='subtitle2' className="subtitleGradientBackgorund">Chamado</Typography>
                                        </Box>
                                        <Divider style={{ marginBottom: 4 }} />
                                        <Grid container alignItems="center" spacing={1}>
                                            {item?.ticket_code ?
                                                <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                    <TextField
                                                        name="ticket_code"
                                                        label='Código'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        disabled
                                                        value={item.ticket_code}
                                                    />
                                                </Grid>
                                                : null}
                                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <TextField
                                                    name="name"
                                                    label='Assunto'
                                                    margin="dense"
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    autoFocus={true}
                                                    value={values.name}
                                                    onChange={handleChange}
                                                    error={!!errors.name && !!dirty.name}
                                                    helperText={!!errors.name && !!dirty.name && errors.name}
                                                    disabled={!!item?.id}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                                                {item?.created_by_user ?
                                                    <TextField
                                                        name="created_by_user"
                                                        label='Criado por'
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        disabled
                                                        value={item.created_by_user.name}
                                                    />
                                                    : null}
                                            </Grid>
                                        </Grid>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <SearchEntity<FinalCustomer, 'id', 'name'>
                                                    name="final_customer_id"
                                                    entity="finalCustomer"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Cliente final"
                                                    value={values.final_customer_id || null}
                                                    style={{ flex: 1 }}
                                                    onChangeHandle={async (_, v) => {
                                                        await setFieldValue('final_customer_id', v);
                                                        await setFieldValue('customer_contacts', []);
                                                        await fetchDataByFinalCustomer(v, false, setFieldValue);
                                                    }}
                                                    required
                                                    isDisabled={user?.type === UserType.FinalCustomer || isStatusClosed}
                                                    extraWhere={user?.type === UserType.FinalCustomer ? undefined : [['integrator_id', '=', user?.integrator_id], ['open_ticket_by_integrator', '=', true]]}
                                                />
                                            </Grid>
                                            {item?.ticket_status_id && <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                <SearchEntity<TicketStatus, 'id', 'name'>
                                                    name="ticket_status_id"
                                                    entity="ticketStatus"
                                                    valueField='id'
                                                    labelField="name"
                                                    label="Status"
                                                    value={item?.ticket_status_id || null}
                                                    onChangeHandle={(_, v) => {

                                                    }}
                                                    isDisabled
                                                    extraWhere={extraWhereTicketStatusId}
                                                />
                                            </Grid>}
                                        </Grid>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                                                <MultiSelect<number>
                                                    name='customer_contacts'
                                                    label={'Contatos do Cliente e/ou do Integrador'}
                                                    onChange={(com) => setFieldValue('customer_contacts', com)}
                                                    options={customerContacts}
                                                    values={values.customer_contacts}
                                                    disabled={isStatusClosed}
                                                    required
                                                />
                                            </Grid>
                                            {item && isStatusClosed ? <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                                                <ServiceTimeCounter
                                                    service_time={item.service_time}
                                                    service_time_start={item.service_time_start}
                                                    service_time_end={item.service_time_end}
                                                    returnInput
                                                />
                                            </Grid> : undefined}
                                        </Grid>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                {/* <TextField
                                                    name="description"
                                                    label={"Descrição"}
                                                    margin="dense"
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    required
                                                    disabled={!!item?.id}
                                                    value={values.description}
                                                    onChange={handleChange}
                                                    error={!!errors.description && !!dirty.description}
                                                    helperText={!!errors.description && !!dirty.description && errors.description}
                                                    inputProps={{
                                                        style: {
                                                            resize: 'vertical', overflow: 'auto', height: heightDescription, minHeight: heightDescription, maxHeight: 999
                                                        }
                                                    }}
                                                /> */}
                                                <SafeDeskTextEditor
                                                    value={values.description}
                                                    onChange={(e) => {
                                                        setFieldValue('description', e.target.value)
                                                    }}
                                                    style={{ resize: 'vertical', overflow: 'auto', height: 250, minHeight: 100, maxHeight: 999 }}
                                                    disabled={!!item?.id}
                                                    placeholder='Descrição'
                                                />
                                            </Grid>
                                        </Grid>
                                        <TicketsAttachmentList
                                            attachments={values.attachments}
                                            downloadAttachment={downloadAttachment}
                                            openAttachmentDescriptionDialog={openAttachmentDescriptionDialog}
                                            removeAttachment={removeAttachment}
                                            setFieldValue={setFieldValue}
                                            addAttachment={addAttachment}
                                            type={AttachmentTaskType.Client}
                                            title={'Anexos'}
                                            isDisabled={!!item?.id}
                                        />
                                        {isStatusClosed ? <>
                                            <Box marginTop={3} marginBottom={0} className='subtitleGradientBackgorund'>
                                                <Typography style={{ flex: 1 }} variant='subtitle2'>Resolução</Typography>
                                            </Box>
                                            <Divider style={{ marginBottom: 4 }} />
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                    <SearchEntity<TicketCause, 'id', 'name'>
                                                        name="ticket_cause_id"
                                                        entity="ticketCause"
                                                        valueField='id'
                                                        labelField="name"
                                                        label="Causa"
                                                        value={item?.ticket_cause_id || null}
                                                        onChangeHandle={(_, v) => { }}
                                                        extraWhere={[['use_in_task', '=', true]]}
                                                        isClearable
                                                        isDisabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                                                    <SearchEntity<TicketSolutionType, 'id', 'name'>
                                                        name="ticket_solution_type_id"
                                                        entity="ticketSolutionType"
                                                        valueField='id'
                                                        labelField="name"
                                                        label="Tipo de solução"
                                                        value={item?.ticket_solution_type_id || null}
                                                        onChangeHandle={(_, v) => { }}
                                                        extraWhere={[['use_in_task', '=', true]]}
                                                        isClearable
                                                        isDisabled
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid container alignItems="center" spacing={1}>
                                                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                                    <TextField
                                                        name="solution"
                                                        label={"Solução"}
                                                        margin="dense"
                                                        variant="outlined"
                                                        fullWidth
                                                        multiline
                                                        value={item?.solution || ''}
                                                        onChange={handleChange}
                                                        disabled
                                                        inputProps={{
                                                            style: {
                                                                resize: 'vertical', overflow: 'auto', height: heightSolution, minHeight: heightSolution, maxHeight: 500
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <TicketsAttachmentList
                                                attachments={values.attachments}
                                                downloadAttachment={downloadAttachment}
                                                openAttachmentDescriptionDialog={openAttachmentDescriptionDialog}
                                                removeAttachment={removeAttachment}
                                                setFieldValue={setFieldValue}
                                                addAttachment={addAttachment}
                                                type={AttachmentTaskType.Solution}
                                                title={'Anexos da resolução'}
                                                isDisabled
                                            />
                                        </> : null}
                                        <TicketsAttachmentDescriptionFormDialog
                                            attachmentDescriptionDialogOpen={attachmentDescriptionDialogOpen}
                                            onCancel={() => closeAttachmentDescriptionDialog()}
                                            onSubmit={(description) => submitAttachmentDescriptionDialog(setFieldValue, values.attachments, description, attachmentsEditingIdx)}
                                            attachments={values.attachments}
                                            attachmentsEditingIdx={attachmentsEditingIdx}
                                        />
                                        <CommentFormDialog
                                            commentDialogOpen={commentDialogOpen}
                                            onCancel={() => closeCommentDialog()}
                                            onSubmit={(comment, isPublic, time, assigned_users, attachments) => submitCommentDialog(setFieldValue, values.comments, comment, isPublic, time, assigned_users, attachments, values.attachments)}
                                            allowPublicComment={false}
                                            onlyPublicComments={true}
                                            useTime={false}
                                            useAssignedToUser={false}
                                            enableAttachments={true}
                                            attachmentType={AttachmentTaskType.Client}
                                        />
                                    </>
                                );
                            }}
                        </Form>
                        <Box zIndex={2} marginTop={3} marginBottom={4} borderBottom={`1px solid ${theme.palette.divider}`} display="flex" position={'sticky'} top={'-4px'} bgcolor={theme.palette.background.default}>
                            <Typography variant='subtitle2' style={{ flex: 1 }} className="subtitleGradientBackgorund">Interações no chamado</Typography>
                            <Tooltip placement="top" title={'Adicionar comentário'}>
                                <IconButton
                                    size='small'
                                    color="inherit"
                                    aria-label={'Adicionar comentário'}
                                    onClick={() => openCommentDialog()}
                                    disabled={!!isStatusClosed}
                                >
                                    <ChatIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <TicketHistory
                            showTotalTimeCounter={false}
                            key={item?.id}
                            type="task"
                            id={item?.id}
                            comments={item?.comments?.filter(c => c.type === CommentTaskType.PreService && c.is_public === true) || []}
                            ticketHistoryEventListener={eventEmmiter}
                            hideAudits
                        />
                    </Grid >
                </Grid>
            </PanelBodyFormContent>
        </Panel>
    )
}